import { useState, useEffect, useCallback } from "react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";
import { Activity, FileText, BarChart3, Shield } from "lucide-react";

import SustainabilityIntelligencePanel from "../dmaic/SustainabilityIntelligencePanel";
import ControlPlanTab from "./control/ControlPlanTab";
import KpiDashboardTab from "./control/KpiDashboardTab";
import ExecSummaryTab from "./control/ExecSummaryTab";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ControlPhase({ projectId, token }) {
  const [loading, setLoading] = useState(true);

  // Control Plan state
  const [controlPlan, setControlPlan] = useState({
    items: [{ process_step: "", characteristic: "", specification: "", measurement_method: "", sample_size: "", frequency: "", control_method: "", reaction_plan: "", responsible: "" }],
    revision: 1,
    approved_by: ""
  });

  // KPIs state
  const [kpis, setKpis] = useState([
    { kpi_id: "", name: "", target: "", unit: "", frequency: "weekly", current_value: "", upper_threshold: "", lower_threshold: "", owner: "", status: "on_track" }
  ]);

  // Executive Summary state
  const [execSummary, setExecSummary] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const response = await fetch(`${API}/projects/${projectId}/control`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        if (data.control_plan?.items?.length) setControlPlan(data.control_plan);
        if (data.kpis?.length) setKpis(data.kpis);
        if (data.executive_summary) setExecSummary(data.executive_summary);
      }
    } catch (error) {
      console.error("Failed to fetch control data:", error);
    } finally {
      setLoading(false);
    }
  }, [projectId, token]);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (loading) {
    return <div className="text-muted-foreground">Loading Control phase...</div>;
  }

  return (
    <div className="space-y-6" data-testid="control-phase">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl font-bold">Control Phase</h1>
          <p className="text-muted-foreground">Sustain improvements and monitor performance</p>
        </div>
      </div>

      <Tabs defaultValue="sustainability" className="space-y-6">
        <TabsList className="bg-card border border-border">
          <TabsTrigger value="sustainability" className="gap-2" data-testid="tab-sustainability">
            <Activity className="w-4 h-4" /> Sustainability Intelligence
          </TabsTrigger>
          <TabsTrigger value="control-plan" className="gap-2" data-testid="tab-control-plan">
            <FileText className="w-4 h-4" /> Control Plan
          </TabsTrigger>
          <TabsTrigger value="kpis" className="gap-2" data-testid="tab-kpis">
            <BarChart3 className="w-4 h-4" /> KPI Dashboard
          </TabsTrigger>
          <TabsTrigger value="summary" className="gap-2" data-testid="tab-exec-summary">
            <Shield className="w-4 h-4" /> Executive Summary
          </TabsTrigger>
        </TabsList>

        <TabsContent value="sustainability" className="space-y-6">
          <SustainabilityIntelligencePanel projectId={projectId} token={token} />
        </TabsContent>

        <TabsContent value="control-plan" className="space-y-6">
          <ControlPlanTab projectId={projectId} token={token} controlPlan={controlPlan} setControlPlan={setControlPlan} />
        </TabsContent>

        <TabsContent value="kpis" className="space-y-6">
          <KpiDashboardTab projectId={projectId} token={token} kpis={kpis} setKpis={setKpis} />
        </TabsContent>

        <TabsContent value="summary" className="space-y-6">
          <ExecSummaryTab projectId={projectId} token={token} execSummary={execSummary} setExecSummary={setExecSummary} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
